import { IntlShape } from "react-intl";
import moment from "moment";

export const KiB = 1024;
export const MiB = KiB * KiB;
export const GiB = KiB * MiB;

/**
 * Checks for valid input and returns 0 if invalid.
 * Returns the floor of the number, to make sure we don't report 100%
 * if value is less than 100.
 */
export function formatPercent(num: number): string {
    if (Number.isNaN(num)) {
        return String(0);
    }
    return String(Math.floor(num));
}

/**
 * Returns the number if it is whole, or the number rounded to a single
 * decimal point if it is not whole, as a string.
 */
export function formatNumber(num: number): string {
    return Number.isInteger(num) ? num.toString() : num.toFixed(1);
}

/**
 * Returns a formatted bytecount with the appropriate translated unit
 * string appended.
 *
 * @param bytecount Bytecount to format.
 * @param intl Internationalization service object from react-intl.
 */
export function formatBytecount(bytecount: number, intl: IntlShape): string {
    if (bytecount >= GiB) {
        const gbCount = formatNumber(bytecount / GiB);
        return intl.formatMessage({ id: "common.unit.gigabytes" }, { value: gbCount });
    }

    if (bytecount >= MiB) {
        const mbCount = formatNumber(bytecount / MiB);
        return intl.formatMessage({ id: "common.unit.megabytes" }, { value: mbCount });
    }

    if (bytecount >= KiB) {
        const kbCount = formatNumber(bytecount / KiB);
        return intl.formatMessage({ id: "common.unit.kilobytes" }, { value: kbCount });
    }

    return intl.formatMessage({ id: "common.unit.bytes" }, { value: bytecount });
}

/**
 * Returns a simple formatted duration string with the provided value and unit string.
 *
 * @param value Value to display
 * @param unit  Unit to display
 * @param intl  Internationalization service object from react-intl
 */
export function formatDurationDescription(
    value: number,
    unit: "seconds" | "minutes" | "hours" | "days" | "months" | "years",
    intl: IntlShape
): string {
    if (value === 1) {
        return intl.formatMessage(
            { id: "common.duration.one" },
            {
                unit: intl.formatMessage({
                    id: `common.duration.unit.${unit}.single`,
                }),
            }
        );
    }

    return intl.formatMessage(
        { id: "common.duration.multiple" },
        {
            value,
            unit: intl.formatMessage({ id: `common.duration.unit.${unit}` }),
        }
    );
}

/**
 * Returns a formatted duration value with the appropriate translated units.
 *
 * @param milliseconds  Length of duration
 * @param minimumUnit   Smallest unit to include in resulting string
 * @param intl          Internationalization service object from react-intl
 */
export function formatDuration(
    milliseconds: number,
    minimumUnit: "seconds" | "minutes" | "hours" | "days" | "months" | "years",
    intl: IntlShape
): string {
    const duration = moment.duration(milliseconds);

    function lessThanOne(unit: string): string {
        return intl.formatMessage(
            { id: "common.duration.lessThanOne" },
            { unit: intl.formatMessage({ id: `common.duration.unit.${unit}.single` }) }
        );
    }

    function getDurationDescription(
        value: number,
        unit: "seconds" | "minutes" | "hours" | "days" | "months" | "years"
    ): string {
        return value < 1 ? "" : formatDurationDescription(value, unit, intl);
    }

    function assembleDescription(descriptionParts: string[]): string {
        return descriptionParts.filter(value => value).join(", ");
    }

    switch (minimumUnit) {
        case "seconds":
            if (duration.asSeconds() < 1) {
                return lessThanOne("seconds");
            }
            break;
        case "minutes":
            if (duration.asMinutes() < 1) {
                return lessThanOne("minutes");
            }
            break;
        case "hours":
            if (duration.asHours() < 1) {
                return lessThanOne("hours");
            }
            break;
        case "days":
            if (duration.asDays() < 1) {
                return lessThanOne("days");
            }
            break;
        case "months":
            if (duration.asMonths() < 1) {
                return lessThanOne("months");
            }
            break;
        case "years":
            if (duration.asYears() < 1) {
                return lessThanOne("years");
            }
            break;
    }

    const parts: string[] = [];

    parts.push(getDurationDescription(duration.years(), "years"));
    if (minimumUnit === "years") {
        return assembleDescription(parts);
    }

    parts.push(getDurationDescription(duration.months(), "months"));
    if (minimumUnit === "months") {
        return assembleDescription(parts);
    }

    parts.push(getDurationDescription(duration.days(), "days"));
    if (minimumUnit === "days") {
        return assembleDescription(parts);
    }

    parts.push(getDurationDescription(duration.hours(), "hours"));
    if (minimumUnit === "hours") {
        return assembleDescription(parts);
    }

    parts.push(getDurationDescription(duration.minutes(), "minutes"));
    if (minimumUnit === "minutes") {
        return assembleDescription(parts);
    }

    parts.push(getDurationDescription(duration.seconds(), "seconds"));
    return assembleDescription(parts);
}

// Adjusts a formatted datetime string to match MOVEit Transfer's existing datetime formatting
// for various locales; these account for differences between .NET datetime formatting and
// javascript datetime formatting.
function adjustDateTimeString(locale: string, dateTimeString: string): string {
    // Remove commas and French à character
    dateTimeString = dateTimeString.replace(/,| (?:à)/, "");

    // Add space after character for Taiwanese
    if (locale === "zh-TW") {
        dateTimeString = dateTimeString.replace(/午/, "午 ");
    }

    return dateTimeString;
}

export function formatTransferLocaleDate(date: Date, intlLocale: string): string {
    let locale = intlLocale;
    const NUMERIC = "numeric";
    const options = {
        year: NUMERIC,
        month: NUMERIC,
        day: NUMERIC,
        hour: locale === "zh-TW" ? "2-digit" : NUMERIC, // zh-TW uses 12 hour time, but two digit hours
        minute: NUMERIC,
        second: NUMERIC,
    };

    if (locale === "zh-CN") {
        // Chinese-Simple should have same format as Japanese
        locale = "ja-JP";
    }

    return adjustDateTimeString(locale, date.toLocaleDateString(locale, options));
}

export function formatTransferLocaleTime(time: Date, intlLocale: string): string {
    let locale = intlLocale;
    const NUMERIC = "numeric";
    const options = {
        hour: locale === "zh-TW" ? "2-digit" : NUMERIC, // zh-TW uses 12 hour time, but two digit hours
        minute: NUMERIC,
    };

    if (locale === "zh-CN") {
        // Chinese-Simple should have same format as Japanese
        locale = "ja-JP";
    }

    return adjustDateTimeString(locale, time.toLocaleTimeString(locale, options));
}
